import React, { useState, useEffect } from 'react';
import { apiService, DashboardStats } from '../services/api';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalOrders: 0,
    totalRevenue: 0,
    pendingOrders: 0,
    completedOrders: 0,
    averageOrderValue: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tempOrderCount, setTempOrderCount] = useState<number>(0);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupMessage, setCleanupMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Fetching dashboard stats...');
        const dashboardStats = await apiService.getDashboardStats();
        console.log('Dashboard stats received:', dashboardStats);
        setStats(dashboardStats);
      } catch (err) {
        console.error('Failed to fetch dashboard stats:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    const fetchTempOrderCount = async () => {
      try {
        const response = await apiService.getTemporaryOrderCount();
        setTempOrderCount(response.count);
      } catch (err) {
        console.error('Failed to fetch temporary order count:', err);
        // Don't show error for this, just set to 0
        setTempOrderCount(0);
      }
    };

    fetchDashboardStats();
    fetchTempOrderCount();
  }, []);

  const handleCleanupExpiredOrders = async () => {
    try {
      setCleanupLoading(true);
      setCleanupMessage(null);
      
      const response = await apiService.cleanupExpiredTemporaryOrders();
      setCleanupMessage(`Successfully cleaned up ${response.deletedCount} expired temporary orders`);
      
      // Refresh the temporary order count
      const countResponse = await apiService.getTemporaryOrderCount();
      setTempOrderCount(countResponse.count);
      
      // Clear message after 5 seconds
      setTimeout(() => setCleanupMessage(null), 5000);
    } catch (err) {
      console.error('Failed to cleanup expired orders:', err);
      setCleanupMessage('Failed to cleanup expired orders');
      setTimeout(() => setCleanupMessage(null), 5000);
    } finally {
      setCleanupLoading(false);
    }
  };

  const statCards = [
    {
      name: 'Total Orders',
      value: stats.totalOrders,
      icon: '📦',
      color: 'bg-blue-500',
    },
    {
      name: 'Total Revenue',
      value: `€${stats.totalRevenue.toLocaleString()}`,
      icon: '💰',
      color: 'bg-green-500',
    },
    {
      name: 'Pending Orders',
      value: stats.pendingOrders,
      icon: '⏳',
      color: 'bg-yellow-500',
    },
    {
      name: 'Completed Orders',
      value: stats.completedOrders,
      icon: '✅',
      color: 'bg-purple-500',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading dashboard data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Welcome to your wine delivery admin panel</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {statCards.map((stat) => (
          <div key={stat.name} className="card">
            <div className="flex items-center">
              <div className={`flex-shrink-0 p-3 rounded-lg ${stat.color}`}>
                <span className="text-2xl">{stat.icon}</span>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">{stat.name}</p>
                <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Average Order Value</h3>
          <div className="flex items-center">
            <div className="text-3xl font-bold text-primary-600">
              €{stats.averageOrderValue.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Temporary Orders Management */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Temporary Orders</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Temporary Orders</p>
                <p className="text-2xl font-bold text-gray-900">{tempOrderCount}</p>
              </div>
              <div className="text-2xl">🕒</div>
            </div>
            
            <div className="pt-2 border-t border-gray-200">
              <button
                onClick={handleCleanupExpiredOrders}
                disabled={cleanupLoading}
                className="w-full text-white font-medium py-2 px-4 rounded-md transition-colors duration-200"
                style={{
                  backgroundColor: cleanupLoading ? '#f87171' : '#dc2626',
                  cursor: cleanupLoading ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (!cleanupLoading) {
                    e.currentTarget.style.backgroundColor = '#b91c1c';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!cleanupLoading) {
                    e.currentTarget.style.backgroundColor = '#dc2626';
                  }
                }}
              >
                {cleanupLoading ? 'Cleaning up...' : 'Cleanup Expired Orders'}
              </button>
              
              {cleanupMessage && (
                <div className={`mt-2 p-2 rounded-md text-sm ${
                  cleanupMessage.includes('Successfully') 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {cleanupMessage}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
